import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';

import { Action, isActionVisibiltyCompatible } from '../../action.model';
import { isLabelledValue } from '../../util';
import { ColumnComponent } from '../column.component';
import { InlineAction, TableRow } from '../shared';

const actionClasses = ['mod-actions-1', 'mod-actions-2', 'mod-actions-3', 'mod-actions-4'];

@Component({
  selector: 'tr[cp-row]',
  template: `
    <!-- Selection -->
    <td *ngIf="!hideSelection" class="table-column table-body-column mod-checkbox"
         [class.mod-panel]="panel" (click)="$event.stopPropagation()">
      <cp-checkbox [handleClick]="true" [cpModel]="isSelected(row)" (checkboxClick)="toggleSelection.emit(row)"></cp-checkbox>
    </td>

    <!-- Actions -->
    <td *ngIf="!hideActions && inlineActions?.length > 0"
         class="table-column table-body-column mod-actions"
         [ngClass]="actionClass">
      <div class="table-column-actions">
        <div *ngFor="let action of inlineActions" class="table-action" (click)="onInlineAction({action: action, row: row}, $event)">
          <cp-link
            *ngIf="action.type === 'executed' && isActionVisible(action, row); else linkAction"
            [icon]="getIcon(action, row)" [label]="getName(action, row)"
          ></cp-link>
          <ng-template #linkAction>
            <cp-link [icon]="getIcon(action, row)" [label]="getName(action, row)" *ngIf="isActionVisible(action, row)"
                     [link]="action.generateLink(row)"></cp-link>
          </ng-template>
        </div>
      </div>
    </td>

    <!-- Data -->
    <td *ngFor="let column of columns; trackBy: trackColumn" class="table-column table-body-column"
         [class.mod-primary]="hasSecondaryRow"
         [class.mod-panel]="panel" [ngClass]="getColumnClass(column)">
      <ng-container *ngIf="!column.template; else cellTemplate">
        {{getDisplayedValue(row, column)}}
      </ng-container>
      <ng-template #cellTemplate>
        <ng-container *ngTemplateOutlet="column.template; context: { $implicit: row, row: row }">
        </ng-container>
      </ng-template>
    </td>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TableBodyRowComponent {
  @Input()
  row: TableRow;

  @Input()
  hasSecondaryRow: boolean;

  @Input()
  columns: ColumnComponent[];

  @Input()
  selection: Map<number | string, TableRow>;

  @Input()
  hideSelection?: boolean;

  @Input()
  hideActions?: boolean;

  @Input()
  inlineActions: Array<Action<TableRow>>;

  @Input()
  panel?: boolean;

  @Output()
  toggleSelection = new EventEmitter<TableRow>();

  @Output()
  inlineAction = new EventEmitter<InlineAction<TableRow>>();

  isSelected(row: TableRow) {
    return this.selection.has(row.key);
  }

  isActionVisible(action: Action<TableRow>, row: TableRow) {
    return (!this.selection.has(row.key) && action.isVisible(row)) ||
            this.selection.size > 0 && isActionVisibiltyCompatible(action, row, this.selection);
  }

  getName(action: Action<TableRow>, value: TableRow) {
    return typeof action.name === 'function' ? action.name(value) : action.name;
  }

  getIcon(action: Action<TableRow>, value: TableRow) {
    return typeof action.icon === 'function' ? action.icon(value) : action.icon;
  }

  onInlineAction(inlineAction: InlineAction<TableRow>, $event: Event) {
    $event.stopPropagation();
    this.inlineAction.emit(inlineAction);
  }

  get actionClass() {
    return actionClasses[this.inlineActions.length - 1];
  }

  getColumnClass(column: ColumnComponent) {
    const bodyClass = column.calculateBodyClass(this.row);
    return column.bodyClass
      ? `${column.columnClass} ${bodyClass}`
      : column.columnClass;
  }

  getDisplayedValue(row: TableRow, column: ColumnComponent) {
    const field = row.$$fields[column.field!];
    if (isLabelledValue(field)) {
      return field.label;
    }
    return field;
  }

  trackColumn = (index: number, item: ColumnComponent) => {
    return index;
  }
}
